import { add, minMicro, multiply, portion, ratioPortion, ZERO } from "./economy-math.js";
import { buildingRule, unitRule } from "./economy-rules.js";
import type {
  BuildingCapacities,
  EconomyBuilding,
  EconomyInput,
  ManufacturingWorkItem,
  MetalLane,
  WreckageCapacities,
} from "./economy-types.js";
import type { Micro } from "./exact-numbers.js";

export interface ManufacturingCapture {
  readonly intended: {
    readonly newConstruction: Micro;
    readonly restoration: Micro;
    readonly assembly: Micro;
  };
  readonly demand: Record<MetalLane, Micro>;
}

export function captureManufacturing(
  input: EconomyInput,
  constructionWork: Micro,
  assemblyWork: Micro,
  producerDemand: Micro,
): ManufacturingCapture {
  const plan = input.manufacturing;
  const newWork = portion(constructionWork, BigInt(plan.constructionSplit.newConstruction), 100n);
  const restoreWork = portion(constructionWork, BigInt(plan.constructionSplit.restoration), 100n);
  const newConstruction = buildingOutput(plan.newConstruction, newWork, "1", input);
  const restoration = buildingOutput(
    plan.restoration,
    restoreWork,
    plan.restorationWorkRatio,
    input,
    true,
  );
  const assembly = unitOutput(plan.assembly, assemblyWork);
  return {
    intended: { newConstruction, restoration, assembly },
    demand: {
      construction: add(
        cost(plan.newConstruction, newConstruction, "1"),
        cost(plan.restoration, restoration, plan.restorationMetalRatio),
      ),
      "producer-factories": producerDemand,
      "troop-assembly": plan.assembly
        ? multiply(assembly, String(unitRule(plan.assembly.target).metalCost))
        : ZERO,
    },
  };
}

export function suppliedManufacturing(
  capture: ManufacturingCapture,
  received: Record<MetalLane, Micro>,
): ManufacturingCapture["intended"] {
  const constructionRatio = (value: Micro) =>
    ratioPortion(value, received.construction, capture.demand.construction);
  return {
    newConstruction: constructionRatio(capture.intended.newConstruction),
    restoration: constructionRatio(capture.intended.restoration),
    assembly: ratioPortion(
      capture.intended.assembly,
      received["troop-assembly"],
      capture.demand["troop-assembly"],
    ),
  };
}

export function wreckCapacity(
  buildings: BuildingCapacities,
  wreckage: WreckageCapacities,
  target: EconomyBuilding,
  requested: Micro,
): { readonly buildings: BuildingCapacities; readonly wreckage: WreckageCapacities } {
  if (requested < 0n) throw new TypeError("Wreckage request cannot be negative");
  const destroyed = minMicro(buildings[target], requested);
  return Object.freeze({
    buildings: Object.freeze({ ...buildings, [target]: (buildings[target] - destroyed) as Micro }),
    wreckage: Object.freeze({ ...wreckage, [target]: add(wreckage[target], destroyed) }),
  });
}

function buildingOutput(
  item: ManufacturingWorkItem<Parameters<typeof buildingRule>[0]> | null,
  work: Micro,
  workRatio: string,
  input: EconomyInput,
  restoring = false,
): Micro {
  if (!item) return ZERO;
  const workCost = multiply(
    (BigInt(buildingRule(item.target).workCost) * 1_000_000n) as Micro,
    workRatio,
  );
  let output = minMicro(item.remaining, portion(work, 1_000_000n, workCost));
  if (restoring) output = minMicro(output, input.wreckage[item.target]);
  return output;
}

function unitOutput(
  item: ManufacturingWorkItem<Parameters<typeof unitRule>[0]> | null,
  work: Micro,
): Micro {
  if (!item) return ZERO;
  return minMicro(item.remaining, portion(work, 1n, BigInt(unitRule(item.target).workCost)));
}

function cost(
  item: ManufacturingWorkItem<Parameters<typeof buildingRule>[0]> | null,
  output: Micro,
  ratio: string,
): Micro {
  return item
    ? multiply(multiply(output, String(buildingRule(item.target).metalCost)), ratio)
    : ZERO;
}
